import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { LOCALES, type Locale } from '@repo/shared';
import { Icon } from '@repo/ui';
import { useTenant } from '../../app/tenant';
import { setLocale } from '../../i18n';

/** "École Supérieure Privée d'Ingénierie de Sousse" → ["École Supérieure Privée", "d'Ingénierie de Sousse"] (design's two-line lockup). */
export function splitInstitutionName(name: string): [string, string] {
  const m = /^(.{8,}?)\s+((?:d'|d’|de |du |des ).+)$/i.exec(name);
  return m ? [m[1] ?? name, m[2] ?? ''] : [name, ''];
}

function LanguageToggle({ variant }: { variant: 'desktop' | 'mobile' }) {
  const { t, i18n } = useTranslation();
  const current = i18n.language as Locale;
  if (variant === 'mobile') {
    const next = LOCALES[(LOCALES.indexOf(current) + 1) % LOCALES.length] ?? 'fr';
    return (
      <ButtonBase
        onClick={() => void setLocale(next)}
        aria-label={`${t('shell.language')}: ${t(`languages.${next}`)}`}
        sx={{
          height: 32,
          px: 1.5,
          borderRadius: '16px',
          border: '1px solid rgba(255,255,255,.55)',
          gap: 0.75,
          fontSize: 14,
          fontWeight: 600,
          color: 'inherit',
        }}
      >
        <Icon name="language" size={18} />
        {t(`languages.short.${current}`)}
      </ButtonBase>
    );
  }
  return (
    <Box
      role="radiogroup"
      aria-label={t('shell.language')}
      sx={{
        display: 'flex',
        border: 1,
        borderColor: 'outline',
        borderRadius: '18px',
        overflow: 'hidden',
        height: 36,
        fontSize: 13,
        fontWeight: 600,
      }}
    >
      {LOCALES.map((l, i) => {
        const on = l === current;
        return (
          <ButtonBase
            key={l}
            role="radio"
            aria-checked={on}
            lang={l}
            onClick={() => void setLocale(l)}
            sx={(th) => ({
              px: 1.75,
              gap: 0.75,
              fontSize: 13,
              fontWeight: 600,
              fontFamily: l === 'ar' ? "'IBM Plex Sans Arabic', sans-serif" : undefined,
              borderInlineStart: i > 0 ? `1px solid ${th.vars.palette.outline}` : undefined,
              bgcolor: on ? th.vars.palette.primary.container : 'transparent',
              color: on ? th.vars.palette.primary.onContainer : th.vars.palette.text.primary,
            })}
          >
            {on && <Icon name="check" size={16} />}
            {t(`languages.short.${l}`)}
          </ButtonBase>
        );
      })}
    </Box>
  );
}

/** Modules listed on the brand panel (design review: the flat panel looked empty). */
const FEATURES = [
  ['grading', 'notes'],
  ['event_available', 'absences'],
  ['payments', 'finance'],
  ['verified', 'documents'],
] as const;
/** The platform console manages schools, not student records: its own promise on the brand panel. */
const PLATFORM_FEATURES = [
  ['domain', 'schools'],
  ['forward_to_inbox', 'invites'],
  ['block', 'suspend'],
  ['lock', 'restricted'],
] as const;

/**
 * Branded layout from ScrLogin. Wide screens (≥ md): primary panel + form; phones and tablets: primary
 * header with the form below. In dark mode the panel uses the deep primary container (not the pale primary).
 */
export function AuthLayout({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const tenant = useTenant();
  const theme = useTheme();
  const compact = useMediaQuery(theme.breakpoints.down('md'));
  const phone = useMediaQuery(theme.breakpoints.down('sm'));
  const [line1, line2] = splitInstitutionName(tenant.name);
  const platform = tenant.kind === 'platform';
  const hero = platform ? 'auth.platform' : 'auth';
  const features = platform ? PLATFORM_FEATURES : FEATURES;
  const year = new Date().getFullYear();
  const logo = (size: number, font: number) => (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: '12px',
        bgcolor: '#fff',
        color: 'primary.main',
        ...theme.applyStyles('dark', { color: 'primary.container' }),
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        font: `700 ${font}px/1 'Inter Variable', Inter, sans-serif`,
        flexShrink: 0,
        boxShadow: '0 1px 2px rgba(0,0,0,.12)',
      }}
    >
      {tenant.shortName}
    </Box>
  );
  /** Brand surface: primary in light, deep primary container in dark; soft light shapes on top. */
  const brand = {
    position: 'relative',
    overflow: 'hidden',
    bgcolor: 'primary.main',
    color: 'primary.contrastText',
    backgroundImage:
      'radial-gradient(circle at 85% 12%, rgba(255,255,255,.14) 0, rgba(255,255,255,0) 38%), radial-gradient(circle at 8% 92%, rgba(255,255,255,.10) 0, rgba(255,255,255,0) 42%), linear-gradient(160deg, rgba(255,255,255,.06), rgba(0,0,0,.18))',
    ...theme.applyStyles('dark', {
      bgcolor: 'primary.container',
      color: 'primary.onContainer',
    }),
  } as const;

  // Tablet: the phone layout left half the screen empty — centred card on the brand background instead.
  if (compact && !phone) {
    return (
      <Box
        sx={{
          ...brand,
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 3,
          p: 4,
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: 480,
            bgcolor: 'background.paper',
            color: 'text.primary',
            borderRadius: '20px',
            boxShadow: '0 16px 48px rgba(0,0,0,.18)',
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: '12px',
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  font: "700 12px/1 'Inter Variable', Inter, sans-serif",
                  flexShrink: 0,
                }}
              >
                {tenant.shortName}
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0, lineHeight: '18px' }}>
                <Box component="span" sx={{ fontWeight: 600, fontSize: 14 }}>
                  {line1}
                </Box>
                {line2 && (
                  <Box component="span" sx={{ fontSize: 13, color: 'text.secondary' }}>
                    {line2}
                  </Box>
                )}
              </Box>
            </Box>
            <LanguageToggle variant="desktop" />
          </Box>
          {children}
        </Box>
        <Box
          component="footer"
          sx={{
            fontSize: 12,
            opacity: 0.85,
            display: 'flex',
            gap: 2,
            flexWrap: 'wrap',
            justifyContent: 'center',
          }}
        >
          <span>{t('auth.footer', { year, institution: tenant.shortName })}</span>
          <span>{tenant.currentYear ? t('common.academicYear', { year: tenant.currentYear }) : ''}</span>
        </Box>
      </Box>
    );
  }

  if (compact) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'background.default',
          fontSize: 16,
          lineHeight: '24px',
        }}
      >
        <Box sx={{ ...brand, px: 3, pt: 2, pb: 4 }}>
          <Box sx={{ maxWidth: 440, mx: 'auto', display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              {logo(44, 12)}
              <LanguageToggle variant="mobile" />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography
                component="h1"
                sx={{ font: 'inherit', fontSize: 26, lineHeight: '32px', fontWeight: 600 }}
              >
                {t(`${hero}.heroTitle`)}
              </Typography>
              <Typography sx={{ fontSize: 14, lineHeight: '20px', opacity: 0.9 }}>{tenant.name}</Typography>
            </Box>
          </Box>
        </Box>
        <Box
          sx={{
            flex: 1,
            px: 3,
            pt: 3.5,
            pb: 3,
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            maxWidth: phone ? 'none' : 488,
            mx: 'auto',
          }}
        >
          {children}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'grid',
        gridTemplateColumns: 'minmax(380px, 42%) 1fr',
        bgcolor: 'background.default',
        color: 'text.primary',
      }}
    >
      <Box
        sx={{
          ...brand,
          px: 7,
          py: 6,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          gap: 4,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75 }}>
          {logo(48, 14)}
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Box component="span" sx={{ fontWeight: 600, fontSize: 15 }}>
              {line1}
            </Box>
            {line2 && (
              <Box component="span" sx={{ fontSize: 13, opacity: 0.85 }}>
                {line2}
              </Box>
            )}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 460 }}>
          <Typography
            component="p"
            sx={{
              fontSize: 40,
              lineHeight: '48px',
              fontWeight: 600,
              letterSpacing: '-0.01em',
              textWrap: 'balance',
            }}
          >
            {t(`${hero}.heroTitle`)}
          </Typography>
          <Typography sx={{ fontSize: 16, lineHeight: '24px', opacity: 0.9, textWrap: 'pretty' }}>
            {t(`${hero}.heroText`)}
          </Typography>
          <Box
            component="ul"
            sx={{ listStyle: 'none', p: 0, m: 0, mt: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}
          >
            {features.map(([icon, key]) => (
              <Box
                component="li"
                key={key}
                sx={{ display: 'flex', alignItems: 'center', gap: 1.5, fontSize: 15 }}
              >
                <Box
                  component="span"
                  aria-hidden
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '10px',
                    bgcolor: 'rgba(255,255,255,.14)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <Icon name={icon} size={20} />
                </Box>
                {t(`${hero}.features.${key}`)}
              </Box>
            ))}
          </Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: 13,
            opacity: 0.85,
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <span>{tenant.currentYear ? t('common.academicYear', { year: tenant.currentYear }) : ''}</span>
          <span>{t('common.poweredBy', { product: tenant.productName })}</span>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', px: 6, pt: 4, pb: 4, minWidth: 0 }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <LanguageToggle variant="desktop" />
        </Box>
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
          {children}
        </Box>
        <Box
          component="footer"
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: 12,
            color: 'text.tertiary',
            gap: 2,
            flexWrap: 'wrap',
            whiteSpace: 'nowrap',
          }}
        >
          <span>{t('auth.footer', { year, institution: tenant.shortName })}</span>
        </Box>
      </Box>
    </Box>
  );
}

/** Phone-style form (bigger fields, compact header) below the md breakpoint. */
export function useIsMobile() {
  const theme = useTheme();
  return useMediaQuery(theme.breakpoints.down('md'));
}

export function useIsPhone() {
  const theme = useTheme();
  return useMediaQuery(theme.breakpoints.down('sm'));
}
